import React, { useState, useLayoutEffect, useContext } from "react";
import LeftBar from "../../components/LeftBar";
import Header from "../../components/Header";
import Player from "../../components/Player";
import "./AppContainer.scss";
import ContentRouter from "./ContentRouter";
import useShareInbound from "../../hooks/useShareInbound";
import { hasPremium, isOwnerId, screenWidth } from '../../constants/function';
import { useSelector, useDispatch } from "react-redux";
import DirectChat from "../../components/DirectChat";
import { useEffect } from "react";
import NoInternet from "../../components/NoInternet";
import { SocketContext } from '../../context/socket';
// Code-split: pulls in the Agora SDKs, which only streamers need.
const Streaming = React.lazy(() => import("../Streaming/Streaming"));
import { useHistory, useLocation } from "react-router-dom";
import { clearSong } from "../../action/songs";
import Advertisement from "../../components/Advertisement";
import emitter from '../../EventEmitter';
import SkDrawer from '../../components/SkDrawer/SkDrawer';
import OutsideClickHandler from 'react-outside-click-handler';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BackToStream from '../../components/BackToStream/BackToStream';

import { act } from '../../constants/common';
import ErrorBoundary from '../../components/ErrorBoundary';
import { checkPremiumStatusAndRefresh } from '../../utils/refreshUserData';

const HEADER_HEIGHT = 64;
const HEADER_HEIGHT_COMPACT = 80;
const SIDEBAR_WIDTH = 220;
const NAV_OVERLAY_BREAKPOINT = 900;

function AppContainer(props) {
  const history = useHistory();
  const location = useLocation();
  useShareInbound(location);
  const dispatch = useDispatch();
  const {
    isStream,
    streamData,
    title,
    goal,
    isNew,
    goalDescription,
    shareUrl,
    music,
    obs,
  } = useSelector((state) => state.stream);

  const [isOnline, setIsOnline] = useState(true);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [isJoined, setIsJoined] = React.useState(false);
  const [tokenData, setTokenData] = React.useState(null);
  const [songData, setSongData] = React.useState({});
  const [userDetail, setUserDetail] = React.useState({
    userName: null,
    userId: null,
    timeStamp: null,
    playlistroomId: null,
  });

  const isImmersiveRoute =
    location.pathname.includes('/LiveVideoStreaming') || location.pathname.includes('/Streaming/');

  const isPremium = hasPremium();
  const [ currentScreenWidth, setCurrentScreenWidth ] = useState(screenWidth());
  const socket = useContext(SocketContext).Socket;

  const { showChat } = useSelector((state) => state.chat);
  const { track } = useSelector((state) => state.streamSong);
  const { count } = useSelector((state) => state.adv);
  const { index } = useSelector((state) => state.songs);
  const songs = useSelector((state) => state.songs);

  const [showAdv, setShowAdv] = React.useState(false);
  const [ isFullScreen, setIsFullScreen ] = React.useState(false);
  const [ isNavOpen, setIsNavOpen ] = useState(screenWidth() > 600);
  const [ isDrawerOpen, setIsDrawerOpen ] = useState(false);
  const [ isChatOpen, setIsChatOpen ] = React.useState(false);
  const [ currentSection, setCurrentSection ] = React.useState( "home" );

  const isSidebarDocked =
    !isImmersiveRoute && isNavOpen && currentScreenWidth > NAV_OVERLAY_BREAKPOINT;

  useLayoutEffect(() => {
    function updateSize() {
      setCurrentScreenWidth( screenWidth() );
      setIsNavOpen( screenWidth() > 600 );
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const handleChatOpen = (e) => {
    setUserDetail({ userName: e.userName, userId: e.userid });
    setIsChatOpen(true);
  };

  React.useEffect(() => {
    emitter.on( act.GO_HOME, goHome );
    emitter.on( act.OPEN_FULL_SCREEN, openFullScreen );
    emitter.on( act.CLOSE_FULL_SCREEN, closeFullScreen );
    emitter.on( act.OPEN_DRAWER, openDrawer );
    emitter.on( act.CLOSE_DRAWER, closeDrawer );
    emitter.on( act.TOGGLE_DRAWER, toggleDrawer );
  },[]);

  React.useEffect(() => {
    setIsOnline(window.navigator.onLine);

    window.addEventListener("online", () => {
      setIsOnline(true);
    });

    window.addEventListener("offline", () => {
      setIsOnline(false);
      if (isStream) {
        history.push("/app/Home");
      }
    });
  },[]);

  useEffect(() => {
    if (!showChat) setIsChatOpen(false);
  }, [showChat]);

  useEffect(() => {
    if (isStream) {
      setIsChatOpen(false);
      if (songs["songs"].length > 0) {
        songs["songs"][index].track.pause();
        dispatch(clearSong());
      }
    }
  }, [isStream]);

  // The song-break ad is triggered by the Player (every few auto-advances) via
  // `updatecount(1)`; the livestream gate sets it too. Advertisement ends it with
  // `updatecount(0)`. The old socket "show-ad" push is gone.
  useEffect(() => {
    setShowAdv(count ? true : false);
    if (isStream && track && !count) {
      track.resumeProcessAudioBuffer();
      track.setVolume(10);
    }
  }, [count]);

  useEffect(() => {
    if (isStream && track && count) {
      track.pauseProcessAudioBuffer();
      track.setVolume(0);
    }
  }, [track]);

  useEffect(() => {
    checkPremiumStatusAndRefresh();

    const handleFocus = () => {
      checkPremiumStatusAndRefresh();
    };

    window.addEventListener('focus', handleFocus);

    const intervalId = setInterval(() => {
      checkPremiumStatusAndRefresh();
    }, 10 * 60 * 1000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, []);

  const goHome = async () => {
    emitter.emit( act.HIDE_STREAM );
    closeDrawer();
    history.push("/app/Home");
  }

  const goUpload = async () => {
    emitter.emit( "hideStream" );
    closeDrawer();
    history.push("/app/UploadMusic");
  }

  const openChatFromNav = () => {
    emitter.emit("openDrawerChat");
    setIsNavOpen(false);
  };

  const openNotificationsFromNav = () => {
    emitter.emit("openDrawerNotifications");
    setIsNavOpen(false);
  };

  const openWalletFromNav = () => {
    emitter.emit("hideStream");
    setIsNavOpen(false);
    history.push("/app/Wallet");
  };

  const openFullScreen = async () => {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      let elem = document.getElementById("outerBox");
      if (elem) {
        try {
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen();
          } else if (elem.webkitEnterFullscreen) {
            await elem.webkitEnterFullscreen();
          }
          setIsFullScreen(true);
        }
        catch(e) {
          console.log(e);
        }
      }
    }
  }

  const closeFullScreen = async () => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.webkitCancelFullScreen) {
          await document.webkitCancelFullScreen();
        }
        setIsFullScreen(false);
      }
      catch(e) {
          console.log(e);
      }
    }
  }

  const openDrawer = () => {
    setIsDrawerOpen( true );
  };

  const closeDrawer = () => {
    setIsDrawerOpen( false );
  };

  const toggleDrawer = () => {
    setIsDrawerOpen( !isDrawerOpen );
  };

  const switchSection = (section) => {
    if ( section === "home" ) { goHome(); return; }
    if ( section === "upload" ) { goUpload(); return; }

      if ( isDrawerOpen && currentSection === section ) {
          closeDrawer();
      } else {
          if ( !isDrawerOpen ) openDrawer();
          history.push("/app/" + section);
          setCurrentSection(section);
      }
  }

  // The app stays mounted while offline and the notice covers it. Swapping it
  // out rebuilt every page when the network came back (waking a laptop), and
  // the rebuilt player started the listener's paused song.
  return (
    <>
        <div id="outerBox">
          <div className="hud-scanlines" />

          {!isImmersiveRoute && (
            <Header
                currentScreenWidth={currentScreenWidth}
                isJoined={isJoined}
                tokenData={tokenData}
                songData={songData}
                toggleNav={() => setIsNavOpen(!isNavOpen)}
                isChatOpen={isChatOpen}
                setIsChatOpen={(e) => { typeof e === "object" ? handleChatOpen(e) : setIsChatOpen(true) }}
                isFullScreen={isFullScreen}
            />
          )}

 <div id="contentBox" className="flex w-full" style={{ paddingTop: isImmersiveRoute ? 0 : (currentScreenWidth <= 800 ? HEADER_HEIGHT_COMPACT : HEADER_HEIGHT) }}>
  {!isStream && !isImmersiveRoute && (
    <LeftBar
      isNavOpen={isNavOpen}
      currentScreenWidth={currentScreenWidth}
      onOpenChat={openChatFromNav}
      onOpenNotifications={openNotificationsFromNav}
      onOpenWallet={openWalletFromNav}
      toggleNavOpen={(e) => { screenWidth() < 600 ? setIsNavOpen(e) : setIsNavOpen(true) } }
      switchSection={ switchSection }
    />
  )}

  { isDrawerOpen &&
      <OutsideClickHandler onOutsideClick={() => switchSection( "home" ) }>
        <SkDrawer {...props} />
      </OutsideClickHandler>
  }

  {isNavOpen && currentScreenWidth <= NAV_OVERLAY_BREAKPOINT && !isImmersiveRoute && (
    <div
      onClick={() => setIsNavOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 9,
      }}
    />
  )}

  {!isStream && (
    <div
      className={isImmersiveRoute ? '' : 'app-content'}
      style={{
        marginLeft: isSidebarDocked ? SIDEBAR_WIDTH : 0,
        width: isSidebarDocked ? `calc(100vw - ${SIDEBAR_WIDTH}px)` : "100vw",
      }}
    >
      <ErrorBoundary>
        <ContentRouter {...props} />
      </ErrorBoundary>
    </div>
  )}

          </div>

          {!isStream && !isImmersiveRoute && songs && songs["songs"].length > 0 ? (
            <Player
              songIndex={songs["index"]}
              prevSongIndex={songs["prevIndex"]}
              songs={songs["songs"]}
              all={songs["all"]}
              showAdv={showAdv}
            />
          ) : null}

          {isChatOpen ? (
            <DirectChat
              title={userDetail["userName"]}
              senderUserId={userDetail["userId"]}
              timeStamp={userDetail["timeStamp"]}
              playlistroomId={userDetail["playlistroomId"]}
              senndUserId={(userName, userId, timeStamp, playlistroomId) =>
                setUserDetail({
                  userName: userName,
                  userId: userId,
                  timeStamp: timeStamp,
                  playlistroomId: playlistroomId,
                })
              }
              onOutsideClick={() => {
                setUserDetail({ userName: null, userId: null });
                setIsChatOpen(false);
              }}
            />
          ) : null}

          {showAdv && !isPremium ? <Advertisement /> : null}

          {isStream ? (
            <React.Suspense fallback={null}>
            <Streaming
              streamData={streamData}
              title={title}
              goal={goal}
              isNew={isNew}
              goalDescription={goalDescription}
              shareUrl={shareUrl}
              music={music}
              isJoined={isJoined}
              setIsJoined={setIsJoined}
              tokenData={tokenData}
              setTokenData={setTokenData}
              songData={songData}
              setSongData={setSongData}
              obs={obs}
            />
            </React.Suspense>
          ) : null}

          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          
          <BackToStream />
        </div>

      <NoInternet
        open={!isOnline}
        onRefresh={() => setIsOnline(window.navigator.onLine)}
      />
    </>
  );
}

export default AppContainer;
